/**
 * The sessions hub's pure rules (docs/design/social-plan.md 3, "Sessions"): which list a session
 * is on, what an RSVP is, when its reminders are due and the calendar file for it. No I/O; the edge
 * decides who gets what, the PWA only renders.
 */
import type { TastingSessionStatus } from "./tastingSession.js";

export const SESSION_SCOPES = ["upcoming", "live", "past"] as const;
export type SessionScope = (typeof SESSION_SCOPES)[number];

/** A draft is something you are still planning, so it belongs with what is coming up. */
export function sessionScopeOf(status: TastingSessionStatus): SessionScope {
  if (status === "live") return "live";
  return status === "ended" ? "past" : "upcoming";
}

export const RSVP_VALUES = ["yes", "maybe", "no"] as const;
export type Rsvp = (typeof RSVP_VALUES)[number];

export function isRsvp(value: unknown): value is Rsvp {
  return typeof value === "string" && (RSVP_VALUES as readonly string[]).includes(value);
}

export const REMINDER_STAGES = ["hour", "start"] as const;
export type ReminderStage = (typeof REMINDER_STAGES)[number];

const MINUTE_MS = 60_000;
const LEAD_MS: Readonly<Record<ReminderStage, number>> = { hour: 60 * MINUTE_MS, start: 0 };

/** How late an alarm may fire and still be worth sending: a reminder an hour ahead that arrives
 * after the party began is noise, so anything older is dropped rather than delivered late. */
export const REMINDER_GRACE_MS = 5 * MINUTE_MS;

export interface DueReminder {
  readonly stage: ReminderStage;
  /** Epoch milliseconds the alarm should fire at. */
  readonly at: number;
}

/**
 * The next reminder still worth arming for a session scheduled at `scheduledAt`, given which stages
 * already went out. Stages whose moment passed more than {@link REMINDER_GRACE_MS} ago are skipped.
 */
export function nextReminder(
  scheduledAt: string,
  done: readonly ReminderStage[],
  now: number,
): DueReminder | null {
  const start = Date.parse(scheduledAt);
  if (Number.isNaN(start)) return null;
  for (const stage of REMINDER_STAGES) {
    if (done.includes(stage)) continue;
    const at = start - LEAD_MS[stage];
    if (at + REMINDER_GRACE_MS >= now) return { stage, at };
  }
  return null;
}

const DEFAULT_DURATION_MINUTES = 180;
/** RFC 5545 limits a content line to 75 octets, continuation lines starting with one space. */
const ICS_LINE_OCTETS = 75;

export interface SessionIcsInput {
  readonly id: string;
  readonly name: string;
  /** ISO-8601 UTC instant the session begins. */
  readonly startsAt: string;
  /** ISO-8601 UTC instant the file was made, as the calendar's DTSTAMP. */
  readonly stamp: string;
  readonly description?: string | undefined;
  readonly url?: string | undefined;
  readonly durationMinutes?: number | undefined;
}

function icsInstant(epoch: number): string {
  return new Date(epoch)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

function icsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n|\r/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");
}

/** Folds on code point boundaries, so a multi-byte character is never split across two lines. */
function fold(line: string): string[] {
  const encoder = new TextEncoder();
  const out: string[] = [];
  let current = "";
  let octets = 0;
  for (const char of line) {
    const size = encoder.encode(char).length;
    // A continuation line spends one octet on its leading space.
    const limit = out.length === 0 ? ICS_LINE_OCTETS : ICS_LINE_OCTETS - 1;
    if (octets + size > limit) {
      out.push(out.length === 0 ? current : ` ${current}`);
      current = "";
      octets = 0;
    }
    current += char;
    octets += size;
  }
  out.push(out.length === 0 ? current : ` ${current}`);
  return out;
}

/** A one-event iCalendar file for a session, with a reminder an hour ahead. Generated on the
 * device, so nothing about the session leaves it for a calendar service. */
export function buildSessionIcs(input: SessionIcsInput): string {
  const start = Date.parse(input.startsAt);
  const end = start + (input.durationMinutes ?? DEFAULT_DURATION_MINUTES) * MINUTE_MS;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//proef//sessions//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:session-${input.id}@proef`,
    `DTSTAMP:${icsInstant(Date.parse(input.stamp))}`,
    `DTSTART:${icsInstant(start)}`,
    `DTEND:${icsInstant(end)}`,
    `SUMMARY:${icsText(input.name)}`,
    ...(input.description === undefined ? [] : [`DESCRIPTION:${icsText(input.description)}`]),
    ...(input.url === undefined ? [] : [`URL:${input.url}`]),
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${icsText(input.name)}`,
    "TRIGGER:-PT1H",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return `${lines.flatMap(fold).join("\r\n")}\r\n`;
}
